// AI Assistant Service for Zed Scholar
// Note: This is a mock implementation. In production, you would integrate with an actual AI API

class AIAssistant {
  constructor() {
    this.isExamMode = false;
    this.conversationHistory = [];
    this.requestCount = 0;
    this.requestTimestamps = [];
    this.rateLimitPerMinute = 15;
    this.maxHistoryLength = 10;
  }

  // Set exam mode status
  setExamMode(isExamMode) {
    this.isExamMode = isExamMode;
  }

  // Check if AI is available
  isAvailable() {
    return !this.isExamMode;
  }

  // Check rate limiting
  checkRateLimit() {
    const now = Date.now();
    const oneMinuteAgo = now - 60000;
    
    // Remove requests older than 1 minute
    this.requestTimestamps = this.requestTimestamps.filter(timestamp => timestamp > oneMinuteAgo);
    
    // Check if under rate limit
    if (this.requestTimestamps.length >= this.rateLimitPerMinute) {
      return false;
    }
    
    // Add current request
    this.requestTimestamps.push(now);
    return true;
  }

  // Clear conversation history
  clearHistory() {
    this.conversationHistory = [];
    this.requestCount = 0;
    this.requestTimestamps = [];
  }

  // Get current conversation history
  getHistory() {
    return this.conversationHistory.slice(-this.maxHistoryLength);
  }

  // List available models (for debugging)
  async listAvailableModels() {
    try {
      const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
      
      if (!apiKey) {
        console.error('No API key configured');
        return [];
      }
      
      const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models?key=${apiKey}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to list models: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('Available Gemini models:', data.models?.map(m => m.name));
      return data.models || [];
    } catch (error) {
      console.error('Error listing models:', error);
      return [];
    }
  }

  // Generate response based on user query
  async generateResponse(query) {
    if (!this.isAvailable()) {
      if (this.isExamMode) {
        return "🚫 AI Assistant is disabled during exams. Focus on your exam questions and submit when ready.";
      } else {
        return "🚫 AI Assistant is not available. Please try again later.";
      }
    }

    // Check rate limiting
    if (!this.checkRateLimit()) {
      return "⏰ Too many requests. Please wait a moment before asking another question.";
    }

    // Add current query to conversation history
    this.conversationHistory.push(`user: ${query}`);
    
    // Keep only recent messages for context (limit to prevent token overflow)
    const recentHistory = this.conversationHistory.slice(-this.maxHistoryLength);
    
    // Create context-aware prompt
    const contextPrompt = recentHistory.join('\n');
    const fullPrompt = `Previous conversation:\n${contextPrompt}\n\nuser: ${query}`;

    const maxRetries = 3;
    let lastError = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
        
        if (!apiKey) {
          return "⚠️ AI Assistant is not properly configured. Please contact support.";
        }
        
        // Use only gemini-2.5-flash model
        const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${apiKey}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contents: [{
              parts: [{
                text: fullPrompt
              }]
            }]
          })
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const errorMessage = errorData.error?.message || response.statusText;
          
          if (response.status === 429) {
            return "⏰ Too many requests to the AI service. Please wait a few minutes before trying again.";
          } else if (response.status === 403) {
            return "🔒 AI Assistant access is forbidden. Please check your API configuration.";
          } else if (response.status >= 500) {
            throw new Error(`Server error: ${errorMessage}`);
          } else {
            throw new Error(`API error: ${errorMessage}`);
          }
        }
        
        const data = await response.json();
        
        if (!data.candidates || !data.candidates[0] || !data.candidates[0].content) {
          return "🤔 I received an unexpected response. Please try rephrasing your question.";
        }
        
        const aiResponse = data.candidates[0].content.parts[0].text;
        
        // Add AI response to conversation history
        this.conversationHistory.push(`ai: ${aiResponse}`);
        
        return aiResponse;
        
      } catch (error) {
        console.error(`AI Assistant error (attempt ${attempt}/${maxRetries}):`, error);
        
        // If it's the last attempt, return a user-friendly error
        if (attempt === maxRetries) {
          if (error.name === 'TypeError' && error.message.includes('fetch')) {
            return "🌐 Network error. Please check your internet connection and try again.";
          } else {
            return "😕 I'm having trouble connecting right now. Please try again in a moment.";
          }
        }
        
        // Wait before retrying (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
      }
    }
    
    // This should never be reached, but just in case
    return "😕 Something went wrong. Please try again.";
  }

  // Get conversation starter suggestions
  getSuggestions() {
    return [
      "Help me with mathematics",
      "Explain science concepts", 
      "English grammar help",
      "Study tips and techniques"
    ];
  }

  // Evaluate short answer for exams using Gemini API
  async evaluateShortAnswer(userAnswer, correctAnswer, question, userLevel) {
    try {
      console.log('🤖 Evaluating short answer with Gemini API...');
      
      // Check rate limiting
      if (!this.checkRateLimit()) {
        console.log('⏰ Rate limit reached, using fallback evaluation');
        return this.fallbackEvaluation(userAnswer, correctAnswer);
      }

      const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
      if (!apiKey) {
        console.error('🔑 Gemini API key not found');
        return this.fallbackEvaluation(userAnswer, correctAnswer);
      }

      // Create evaluation prompt
      const evaluationPrompt = `
As an expert educator for ${userLevel} students, evaluate this short answer:

Question: ${question.question}
Expected Answer: ${correctAnswer}
Student's Answer: ${userAnswer}

Evaluate the student's answer and provide:
1. A score from 0.0 to 1.0 (where 1.0 is perfect)
2. Whether it's correct (true/false)
3. Brief feedback (optional)

Respond in JSON format:
{
  "score": 0.8,
  "isCorrect": true,
  "feedback": "Good understanding, minor details missing"
}

Be fair but thorough in evaluation. Consider partial credit for partially correct answers, but the final determination should be either correct (full marks) or incorrect (no marks). Use a 0.7 threshold for correctness.
`;

      const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${apiKey}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contents: [{
            parts: [{
              text: evaluationPrompt
            }]
          }]
        })
      });
      
      if (!response.ok) {
        console.error('🤖 Gemini API error:', response.status, response.statusText);
        return this.fallbackEvaluation(userAnswer, correctAnswer);
      }

      const data = await response.json();
      const text = data.candidates?.[0]?.content?.parts?.[0]?.text;
      
      if (!text) {
        console.error('🤖 No response from Gemini API');
        return this.fallbackEvaluation(userAnswer, correctAnswer);
      }

      // Parse JSON response
      let evaluation;
      try {
        // Extract JSON from response (might have extra text)
        const jsonMatch = text.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          evaluation = JSON.parse(jsonMatch[0]);
        } else {
          throw new Error('No JSON found in response');
        }
      } catch (parseError) {
        console.error('🤖 Failed to parse Gemini response:', parseError);
        return this.fallbackEvaluation(userAnswer, correctAnswer);
      }

      // Validate and normalize response
      return {
        score: Math.max(0, Math.min(1, parseFloat(evaluation.score) || 0)),
        isCorrect: Boolean(evaluation.isCorrect),
        feedback: evaluation.feedback || ''
      };

    } catch (error) {
      console.error('🤖 AI evaluation failed, using fallback:', error);
      return this.fallbackEvaluation(userAnswer, correctAnswer);
    }
  }

  // Fallback evaluation method
  fallbackEvaluation(userAnswer, correctAnswer) {
    const userLower = userAnswer.toLowerCase().trim();
    const correctLower = correctAnswer.toLowerCase().trim();
    
    // Exact match gets full score
    if (userLower === correctLower) {
      return { isCorrect: true, score: 1.0, feedback: 'Perfect answer!' };
    }
    
    // Partial keyword matching for partial credit
    const correctWords = correctLower.split(' ').filter(word => word.length > 2);
    const userWords = userLower.split(' ').filter(word => word.length > 2);
    const matchingWords = correctWords.filter(word => 
      userWords.some(userWord => 
        userWord.includes(word) || word.includes(userWord) || 
        this.levenshteinDistance(userWord, word) <= 1
      )
    );
    
    const matchRatio = matchingWords.length / correctWords.length;
    const score = matchRatio;
    
    return {
      isCorrect: matchRatio >= 0.7,
      score: score,
      feedback: score >= 0.7 ? 'Correct!' : 'Incorrect'
    };
  }

  // Simple Levenshtein distance for fuzzy matching
  levenshteinDistance(str1, str2) {
    const matrix = [];
    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }
    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }
    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }
    return matrix[str2.length][str1.length];
  }
}

// Create singleton instance
const aiAssistant = new AIAssistant();

// Expose the listAvailableModels method for debugging
window.listGeminiModels = () => aiAssistant.listAvailableModels();

export default aiAssistant;
